import React, { useCallback, useContext, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Nav, Row, Spinner, Table } from "react-bootstrap";
import axios from "axios";
import { TranslatorContext } from "../../context/Translator";
import PageLayout from "../../layouts/PageLayout";
import SubmitPaymentModal from "../mplacer/SubmitPaymentModal";

// Store seller's own ledger. Same numbers as the admin panel's store seller
// page (both come from LedgerService on the backend).

const money = (value) =>
    value === null || value === undefined
        ? "—"
        : `Rs ${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (value) => (value ? String(value).slice(0, 10) : "-");

const bucketBadge = (bucket, t) => {
    switch (bucket) {
        case "earned":
            return <Badge bg="success">{t("Counted")}</Badge>;
        case "hold":
            return <Badge bg="warning" text="dark">{t("On hold")}</Badge>;
        case "awaiting_details":
            return <Badge bg="secondary">{t("Waiting for order details")}</Badge>;
        default:
            return <Badge bg="light" text="dark">{t("Not counted (returned)")}</Badge>;
    }
};

const statusBadge = (status, t) => {
    const s = (status || "pending").toLowerCase();
    if (s === "approved") return <Badge bg="success">{t("Approved")}</Badge>;
    if (s === "cancelled") return <Badge bg="secondary">{t("Rejected")}</Badge>;
    return <Badge bg="warning" text="dark">{t("Waiting for approval")}</Badge>;
};

export default function StoreSellerLedgerPage() {
    const { t } = useContext(TranslatorContext);
    const [activeTab, setActiveTab] = useState("summary");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [showSubmit, setShowSubmit] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/store-seller/ledger`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}`, Accept: "application/json" },
            });
            if (!res.data?.success) throw new Error(res.data?.message || "Failed to load ledger.");
            setData(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || err.message || t("Could not load your ledger."));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        load();
    }, [load]);

    const summary = data?.summary || {};
    const items = data?.items || [];
    const payments = data?.payments || [];
    const receivable = summary.settlement_type === "receivable";
    const payable = summary.settlement_type === "payable";
    const canSubmit = summary.who_adds_payment === "self";
    const available = Number(summary.remaining_amount || 0) - Number(summary.pending_amount || 0);

    const rows = [
        { label: t("Your share from delivered orders"), value: summary.store_seller_share },
        { label: t("Company commission on delivered orders"), value: summary.company_commission },
        { label: receivable ? t("You owe the company (delivered orders)") : t("Company owes you (delivered orders)"), value: summary.earned_amount },
        { label: t("Opening balance"), value: summary.opening_balance },
        { label: t("Total"), value: summary.total_amount, strong: true },
        { label: receivable ? t("Paid by you (approved)") : t("Paid to you (approved)"), value: summary.settled_amount },
        { label: t("Waiting for approval"), value: summary.pending_amount },
        { label: receivable ? t("Remaining to pay") : t("Remaining to receive"), value: summary.remaining_amount, strong: true },
        { label: t("Extra"), value: summary.extra_amount },
        { label: t("On hold (pending / shipped orders)"), value: summary.hold_amount },
    ];

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                            <div>
                                <h3 className="fw-bold mb-1">{t("My Ledger")}</h3>
                                <p className="text-muted mb-0">
                                    {summary.payment_value != null
                                        ? t("Your share is the sale price minus the company commission of") + ` ${Number(summary.payment_value)}%` +
                                          (summary.storage_preference === "own_store" ? t(", minus shipping (you keep the product in your own store).") : t(". The company pays shipping."))
                                        : t("Your earnings, payments and balance.")}
                                </p>
                            </div>
                            {canSubmit && (
                                <div className="text-end">
                                    <Button variant="success" disabled={available <= 0} onClick={() => setShowSubmit(true)}>
                                        {t("Add payment you sent")}
                                    </Button>
                                    <div className="small text-muted mt-1">{t("The admin approves it before your balance changes.")}</div>
                                </div>
                            )}
                            {payable && (
                                <div className="small text-muted text-end" style={{ maxWidth: "280px" }}>
                                    {t("The company adds the payments it makes to you. They count once approved.")}
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {error && <Alert variant="danger">{error}</Alert>}
                    {notice && <Alert variant="success" dismissible onClose={() => setNotice("")}>{notice}</Alert>}

                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom-0 pt-3">
                            <Nav variant="tabs" activeKey={activeTab} onSelect={(tab) => tab && setActiveTab(tab)}>
                                <Nav.Item><Nav.Link eventKey="summary">{t("Summary")}</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="items">{t("Sold items")} ({items.length})</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="payments">{t("Payments")} ({payments.length})</Nav.Link></Nav.Item>
                            </Nav>
                        </Card.Header>
                        <Card.Body>
                            {loading ? (
                                <div className="text-center py-5"><Spinner animation="border" /></div>
                            ) : activeTab === "summary" ? (
                                <Row className="justify-content-center">
                                    <Col lg={8}>
                                        <div className={`p-3 rounded-3 mb-3 ${receivable ? "bg-warning-subtle" : payable ? "bg-success-subtle" : "bg-light"}`}>
                                            <div className="fw-bold">
                                                {receivable ? t("Receivable: you collect the money and pay the company") : payable ? t("Payable: the company pays you") : t("Payable or receivable is not set yet")}
                                            </div>
                                            <div className="small text-muted">{summary.payment_status_message}</div>
                                        </div>
                                        {rows.map((row) => (
                                            <div key={row.label} className="d-flex justify-content-between py-2 border-bottom">
                                                <span className={row.strong ? "fw-semibold" : "text-muted"}>{row.label}</span>
                                                <span className={row.strong ? "fw-bold" : ""}>{money(row.value)}</span>
                                            </div>
                                        ))}
                                        <div className="small text-muted mt-2">
                                            {t("Delivered orders count. Pending and shipped orders are on hold. Returned orders do not count.")}
                                        </div>
                                    </Col>
                                </Row>
                            ) : activeTab === "items" ? (
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>{t("Order")}</th>
                                                <th>{t("Product")}</th>
                                                <th>{t("Delivery")}</th>
                                                <th className="text-end">{t("Sale")}</th>
                                                <th className="text-end">{t("Company commission")}</th>
                                                <th className="text-end">{t("Your share")}</th>
                                                <th>{t("Ledger")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.length ? items.map((item) => (
                                                <tr key={item.id}>
                                                    <td className="fw-semibold">{item.order_number}</td>
                                                    <td>{item.product_code}<div className="small text-muted">{item.title}</div></td>
                                                    <td>{item.delivery_status}</td>
                                                    <td className="text-end">{money(item.sale_pkr)}</td>
                                                    <td className="text-end">{money(item.company_commission_pkr)}</td>
                                                    <td className="text-end fw-semibold">{money(item.share_pkr)}</td>
                                                    <td>{bucketBadge(item.ledger_bucket, t)}</td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan={7} className="text-center text-muted py-5">{t("No sold items yet.")}</td></tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>{t("Date")}</th>
                                                <th>{t("Status")}</th>
                                                <th>{t("Added by")}</th>
                                                <th>{t("Bank")}</th>
                                                <th>{t("Notes")}</th>
                                                <th className="text-end">{t("Amount")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {payments.length ? payments.map((payment) => (
                                                <tr key={payment.id}>
                                                    <td>{formatDate(payment.payment_date)}</td>
                                                    <td>{statusBadge(payment.status, t)}</td>
                                                    <td>{payment.added_by_self ? t("You") : payment.creator?.name || t("Company")}</td>
                                                    <td>{payment.bank?.name || "-"}</td>
                                                    <td>{payment.notes || "-"}</td>
                                                    <td className="text-end fw-semibold">{money(payment.amount)}</td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan={6} className="text-center text-muted py-5">{t("No payments yet.")}</td></tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    <SubmitPaymentModal
                        show={showSubmit}
                        onHide={() => setShowSubmit(false)}
                        summary={summary}
                        banksUrl={`${process.env.REACT_APP_API_URL}/store-seller/payment-banks`}
                        submitUrl={`${process.env.REACT_APP_API_URL}/store-seller/ledger/payments`}
                        onSuccess={(msg) => {
                            setNotice(msg);
                            setActiveTab("payments");
                            load();
                        }}
                        t={t}
                    />
                </Col>
            </Row>
        </PageLayout>
    );
}
